import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, Loader2, DollarSign, User, Calendar, 
  FileText, AlertCircle, CheckCircle, XCircle, 
  RefreshCw, Send, Undo2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentView = () => {
  const navigate = useNavigate();
  const { paymentNumber } = useParams();
  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPosting, setIsPosting] = useState(false);
  const [isReversing, setIsReversing] = useState(false);
  const [customers, setCustomers] = useState([]);


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [paymentRes, custRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ar/payments/${paymentNumber}`),
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }))
      ]);
      setPayment(paymentRes.data);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading payment:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load payment: ${errorMsg}`);
      navigate('/ar/payments');
    } finally {
      setIsLoading(false);
    }
  }, [paymentNumber, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-gray-100 text-gray-800';
      case 'POSTED': return 'bg-green-100 text-green-800';
      case 'CLEARED': return 'bg-orange-100 text-orange-800';
      case 'REVERSED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'POSTED': case 'CLEARED': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'REVERSED': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <DollarSign className="w-5 h-5 text-orange-600" />;
    }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'BANK_TRANSFER': 'Bank Transfer',
      'CHECK': 'Check',
      'CASH': 'Cash',
      'CREDIT_CARD': 'Credit Card',
      'DIRECT_DEBIT': 'Direct Debit'
    };
    return labels[method] || method;
  };

  const handlePost = async () => {
    if (!window.confirm('Are you sure you want to post this payment? This will clear the allocated open items.')) {
      return;
    }

    setIsPosting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ar/payments/${paymentNumber}/post`);
      toast.success('Payment posted successfully! Open items have been cleared.');
      loadData();
    } catch (error) {
      console.error('Error posting payment:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to post payment: ${errorMsg}`);
    } finally {
      setIsPosting(false);
    }
  };

  const handleReverse = async () => {
    if (!window.confirm('Are you sure you want to reverse this payment? This will reopen the cleared items.')) {
      return;
    }

    setIsReversing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ar/payments/${paymentNumber}/reverse`);
      toast.success('Payment reversed successfully! Open items have been reopened.');
      loadData();
    } catch (error) {
      console.error('Error reversing payment:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to reverse payment: ${errorMsg}`);
    } finally {
      setIsReversing(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading payment...</span>
        </div>
      </Layout>
    );
  }

  if (!payment) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-gray-600">Payment not found</p>
          <Button onClick={() => navigate('/ar/payments')} className="mt-4">
            Back to Payments
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/ar/payments')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Payment {payment.payment_number}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(payment.status)}`}>
                  {getStatusIcon(payment.status)}
                  {payment.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Customer: {getCustomerName(payment.customer_id)} | Date: {payment.payment_date?.split('T')[0]}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {payment.status === 'DRAFT' && (
              <Button onClick={handlePost} disabled={isPosting} data-testid="post-payment-btn">
                {isPosting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Post Payment
                  </>
                )}
              </Button>
            )}
            {payment.status === 'POSTED' && (
              <Button variant="destructive" onClick={handleReverse} disabled={isReversing} data-testid="reverse-payment-btn">
                {isReversing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Reversing...
                  </>
                ) : (
                  <>
                    <Undo2 className="w-4 h-4 mr-2" />
                    Reverse
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Details */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Payment Details
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="font-medium">{getPaymentMethodLabel(payment.payment_method)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Payment Date</p>
                  <p className="font-medium">{payment.payment_date?.split('T')[0]}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Value Date</p>
                  <p className="font-medium">{payment.value_date?.split('T')[0] || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bank Reference</p>
                  <p className="font-medium">{payment.bank_reference || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Bank Account</p>
                  <p className="font-medium">{payment.bank_account || '-'}</p>
                </div>
                {payment.accounting_document && (
                  <div>
                    <p className="text-xs text-gray-500">Accounting Document</p>
                    <p className="font-mono font-medium text-orange-600">{payment.accounting_document}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Customer Info */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Customer
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Customer ID</p>
                  <p className="font-mono font-medium">{payment.customer_id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Customer Name</p>
                  <p className="font-medium">{getCustomerName(payment.customer_id)}</p>
                </div>
              </div>
            </Card>

            {/* Allocations */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Allocations ({payment.allocations?.length || 0})
              </h3>
              {payment.allocations && payment.allocations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="allocations-table">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 text-sm font-semibold">Invoice</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Original Amount</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Allocated</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payment.allocations.map((alloc, index) => (
                        <tr key={alloc.allocation_id || alloc.invoice_id || `alloc-${index}`} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3">
                            <span 
                              className="font-mono text-orange-600 cursor-pointer hover:underline"
                              onClick={() => navigate(`/otc/billing/${alloc.billing_number}`)}
                            >
                              {alloc.billing_number}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-mono">
                            USD {alloc.original_amount?.toFixed(2)}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold text-green-600">
                            USD {alloc.allocated_amount?.toFixed(2)}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">
                            USD {alloc.remaining_amount?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No allocations</p>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Amount Summary */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Amount Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Amount:</span>
                  <span className="font-mono font-bold">{payment.currency} {payment.payment_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Allocated:</span>
                  <span className="font-mono">{payment.currency} {payment.allocated_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t">
                  <span className="font-semibold">Unallocated:</span>
                  <span className={`font-mono font-bold ${payment.unallocated_amount > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {payment.currency} {payment.unallocated_amount?.toFixed(2)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Status Flow */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Status Flow</h3>
              <div className="space-y-2">
                {['DRAFT', 'POSTED', 'REVERSED'].map((status, idx) => {
                  const isActive = payment.status === status;
                  const isPassed = payment.status === 'POSTED' && status === 'DRAFT';
                  const isReversed = payment.status === 'REVERSED' && ['DRAFT', 'POSTED'].includes(status);
                  
                  return (
                    <div key={status} className={`flex items-center gap-2 p-2 rounded ${isActive ? 'bg-orange-100' : ''}`}>
                      <div className={`w-4 h-4 rounded-full ${
                        isActive ? 'bg-orange-500' : 
                        isPassed || isReversed ? 'bg-green-500' : 
                        'bg-gray-300'
                      }`} />
                      <span className={`text-sm ${
                        isActive ? 'font-bold text-orange-800' : 
                        isPassed || isReversed ? 'text-green-700' : 
                        'text-gray-500'
                      }`}>
                        {status}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Timestamps */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Timestamps
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Created</p>
                  <p className="text-sm">{payment.created_at?.split('T')[0]} {payment.created_at?.split('T')[1]?.split('.')[0]}</p>
                </div>
                {payment.posted_at && (
                  <div>
                    <p className="text-xs text-gray-500">Posted</p>
                    <p className="text-sm">{payment.posted_at?.split('T')[0]} {payment.posted_at?.split('T')[1]?.split('.')[0]}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Notes */}
            {payment.payment_note && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{payment.payment_note}</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentView;
